import React, { useEffect, useState } from 'react';
import { getUsers, createUser, updateUser, deleteUser, changePassword,
         resetUserMfa, resetUserPasswordGenerated } from '../../api';
import { useToast } from '../../components/Toast';

const ROLE_LABELS = { startup: 'Startup', investisseur: 'Investisseur', jury: 'Membre du Jury', analyste: 'Analyste', admin: 'Administrateur' };
const ROLE_COLORS = { startup: '#2E75B6', investisseur: '#27AE60', jury: '#9B59B6', analyste: '#E67E22', admin: '#E74C3C' };

// Bouton d'action reduit a une icone, avec une bulle d'aide au survol.
//
// La bulle est en `position: fixed`, positionnee a partir du rectangle du
// bouton : la carte du tableau est un conteneur `overflow-x: auto`, qui
// rognerait une bulle positionnee en absolu (et ferait apparaitre des barres
// de defilement parasites).
//
// `title` est conserve en plus de la bulle : il porte l'infobulle native pour
// les utilisateurs au clavier et les lecteurs d'ecran s'appuient sur
// `aria-label`.
function IconAction({ icon, hint, onClick, disabled, borderColor, background, danger }) {
  const [pos, setPos] = useState(null);
  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: r.left + r.width / 2, y: r.top });
  };
  return (
    <>
      <button type="button" onClick={onClick} disabled={disabled} title={hint} aria-label={hint}
        onMouseEnter={show} onMouseLeave={() => setPos(null)}
        onFocus={show} onBlur={() => setPos(null)}
        style={{
          padding: '4px 9px', borderRadius: 6, fontSize: 13, lineHeight: 1.4,
          fontFamily: 'inherit',
          border: `1px solid ${borderColor || (danger ? '#FFCDD2' : 'var(--border)')}`,
          background: background || (danger ? '#FFF5F5' : 'white'),
          opacity: disabled ? 0.35 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}>
        {icon}
      </button>
      {pos && (
        <span role="tooltip" style={{
          position: 'fixed', left: pos.x, top: pos.y - 8, transform: 'translate(-50%, -100%)',
          background: '#1B1E38', color: 'white', padding: '6px 10px', borderRadius: 6,
          fontSize: 12, lineHeight: 1.4, maxWidth: 260, whiteSpace: 'normal', textAlign: 'center',
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)', zIndex: 2000, pointerEvents: 'none',
        }}>
          {hint}
        </span>
      )}
    </>
  );
}

// Etat du second facteur, dans l'ordre de priorite : une demande d'assistance
// ouverte prime sur tout le reste, c'est ce qui appelle une action de l'admin.
function mfaBadge(u) {
  const pill = (bg, color, border, label, title) => (
    <span title={title} style={{ fontSize: 10.5, padding: '2px 8px', borderRadius: 10,
      background: bg, color, border: `1px solid ${border}`, whiteSpace: 'nowrap' }}>{label}</span>
  );
  if (u.mfa_help_pending) return pill('#FFEBEE', '#C62828', '#FFCDD2', 'bloqué', "L'utilisateur a demandé de l'aide : réinitialisez son second facteur");
  if (!u.mfa_required) return <span style={{ color: 'var(--text-light)', fontSize: 12 }}>—</span>;
  if (u.must_change_password) return pill('#FFF3E0', '#B26A00', '#FFCC80', 'mot de passe à changer', "Le compte n'a pas encore été activé par son titulaire");
  if (u.mfa_enabled) return pill('#E8F5E9', '#2E7D32', '#A5D6A7', 'actif', 'Application d’authentification enrôlée');
  return pill('#FFF3E0', '#B26A00', '#FFCC80', 'à enrôler', "Le second facteur sera configuré à la prochaine connexion");
}

function ConfirmModal({ open, title, message, confirmLabel, confirmColor, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onCancel}>
      <div style={{ background: 'white', borderRadius: 16, padding: '28px 32px', maxWidth: 440, width: '90%',
        boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: 17 }}>{title}</h3>
        <p style={{ fontSize: 14, color: 'var(--text-light)', lineHeight: 1.6, marginBottom: 24 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel}
            style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 500 }}>
            Annuler
          </button>
          <button onClick={onConfirm}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none',
              background: confirmColor || '#E74C3C', color: 'white', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 600 }}>
            {confirmLabel || 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', role: 'analyste', first_name: '', last_name: '', company: '' });
  const [juryInviteSent, setJuryInviteSent] = useState(false);
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmLabel, confirmColor, onConfirm }
  // Mot de passe genere, affiche une seule fois apres creation ou reinitialisation.
  const [generated, setGenerated] = useState(null); // { email, password, isNew }

  const load = () => getUsers().then(setUsers).catch(console.error);
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [u.first_name, u.last_name, u.email, u.company, ROLE_LABELS[u.role]]
      .some(v => (v || '').toLowerCase().includes(q));
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await createUser(form);
      setShowForm(false);
      setForm({ email: '', password: '', role: 'analyste', first_name: '', last_name: '', company: '' });
      setJuryInviteSent(false);
      load();
      if (created.generated_password) {
        setGenerated({ email: created.email, password: created.generated_password, isNew: true });
      }
      toast.success('Utilisateur créé avec succès');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const askResetMfa = (u) => setConfirmModal({
    title: 'Réinitialiser la double authentification',
    message: `${u.first_name || ''} ${u.last_name || ''} (${u.email}) devra enregistrer `
      + `à nouveau son application d'authentification à sa prochaine connexion, avec un `
      + `nouveau QR code et de nouveaux codes de secours.\n\n`
      + `Le compte reste actif et son mot de passe est inchangé.`,
    confirmLabel: 'Réinitialiser',
    confirmColor: '#E67E22',
    onConfirm: async () => {
      setConfirmModal(null);
      try {
        await resetUserMfa(u.id);
        load();
        toast.success('Double authentification réinitialisée');
      } catch (err) { toast.error(err.message); }
    },
  });

  const askResetPassword = (u) => setConfirmModal({
    title: 'Générer un nouveau mot de passe',
    message: `Un mot de passe de 12 caractères sera généré pour ${u.email}. Il sera affiché `
      + `une seule fois, à vous de le lui transmettre. L'utilisateur devra en choisir un `
      + `autre à sa prochaine connexion.\n\nSon mot de passe actuel cessera de fonctionner.`,
    confirmLabel: 'Générer',
    confirmColor: '#E67E22',
    onConfirm: async () => {
      setConfirmModal(null);
      try {
        const res = await resetUserPasswordGenerated(u.id);
        setGenerated({ email: res.email, password: res.generated_password, isNew: false });
        load();
      } catch (err) { toast.error(err.message); }
    },
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    // Controles cote client : feedback immediat. Le backend refait les memes
    // verifications (il reste la seule autorite en la matiere).
    if (pwdForm.next.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }
    setPwdSaving(true);
    try {
      await changePassword(pwdForm.current, pwdForm.next);
      setPwdForm({ current: '', next: '', confirm: '' });
      setShowPwdForm(false);
      toast.success('Mot de passe modifié avec succès');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPwdSaving(false);
    }
  };

  const askToggleActive = (user) => {
    const action = user.is_active ? 'désactiver' : 'activer';
    setConfirmModal({
      title: `${user.is_active ? 'Désactiver' : 'Activer'} l'utilisateur`,
      message: `Voulez-vous vraiment ${action} le compte de ${user.first_name || ''} ${user.last_name || ''} (${user.email}) ?${user.is_active ? ' L\'utilisateur ne pourra plus se connecter.' : ''}`,
      confirmLabel: user.is_active ? 'Désactiver' : 'Activer',
      confirmColor: user.is_active ? '#E67E22' : '#27AE60',
      onConfirm: async () => {
        try {
          await updateUser(user.id, { is_active: !user.is_active });
          load();
          toast.success(`Utilisateur ${user.is_active ? 'désactivé' : 'activé'}`);
        } catch (err) { toast.error(err.message); }
        setConfirmModal(null);
      },
    });
  };

  const askDelete = (user) => {
    setConfirmModal({
      title: 'Supprimer l\'utilisateur',
      message: `Voulez-vous vraiment supprimer définitivement le compte de ${user.first_name || ''} ${user.last_name || ''} (${user.email}) ?\n\nCette action est irréversible. Toutes les données associées (favoris, alertes, commentaires jury) seront supprimées.`,
      confirmLabel: 'Supprimer définitivement',
      confirmColor: '#E74C3C',
      onConfirm: async () => {
        try {
          await deleteUser(user.id);
          load();
          toast.success(`Utilisateur ${user.email} supprimé`);
        } catch (err) { toast.error(err.message); }
        setConfirmModal(null);
      },
    });
  };

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1>Gestion des Utilisateurs</h1>
          <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
            {showForm ? 'Annuler' : 'Nouvel utilisateur'}
          </button>
        </div>

        {/* ----- Mon compte : changement de mot de passe ----- */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div onClick={() => setShowPwdForm(!showPwdForm)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
            <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              🔑 Mon compte — Modifier mon mot de passe
            </h3>
            <span style={{ fontSize: 18, color: 'var(--primary)', transform: showPwdForm ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
          </div>
          {showPwdForm && (
            <form onSubmit={handleChangePassword} style={{ marginTop: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="form-label">Mot de passe actuel</label>
                  <input type="password" autoComplete="current-password" value={pwdForm.current}
                    onChange={e => setPwdForm(f => ({ ...f, current: e.target.value }))}
                    className="form-input" required />
                </div>
                <div>
                  <label className="form-label">Nouveau mot de passe</label>
                  <input type="password" autoComplete="new-password" minLength={8} value={pwdForm.next}
                    onChange={e => setPwdForm(f => ({ ...f, next: e.target.value }))}
                    className="form-input" required />
                </div>
                <div>
                  <label className="form-label">Confirmer le nouveau</label>
                  <input type="password" autoComplete="new-password" minLength={8} value={pwdForm.confirm}
                    onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))}
                    className="form-input" required />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button type="submit" className="btn btn-accent" disabled={pwdSaving}>
                  {pwdSaving ? 'Modification…' : 'Modifier mon mot de passe'}
                </button>
                <span style={{ fontSize: 12, color: 'var(--text-light)' }}>
                  8 caractères minimum. Le mot de passe actuel est exigé pour valider le changement.
                </span>
              </div>
            </form>
          )}
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 16 }}>Créer un utilisateur</h3>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="form-label">Prénom</label>
                  <input type="text" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="form-input" required />
                </div>
                <div>
                  <label className="form-label">Nom</label>
                  <input type="text" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="form-input" required />
                </div>
                <div>
                  <label className="form-label">Rôle</label>
                  <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="form-input">
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label className="form-label">Email</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="form-input" required />
                </div>
                <div>
                  <label className="form-label">Mot de passe</label>
                  {form.role === 'jury' ? (
                    <div style={{ fontSize: 12.5, color: 'var(--text-light)', padding: '10px 0', lineHeight: 1.5 }}>
                      Généré automatiquement (12 caractères) et affiché une seule fois
                      après la création.
                    </div>
                  ) : (
                    <input type="password" value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      className="form-input" required />
                  )}
                </div>
                <div>
                  <label className="form-label">Société</label>
                  <input type="text" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} className="form-input" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button type="submit" className="btn btn-accent">Créer</button>
                {form.role === 'jury' && (
                  juryInviteSent ? (
                    <span style={{ fontSize: 13, color: '#27AE60', fontWeight: 600 }}>
                      Invitation envoyée par email au membre du jury
                    </span>
                  ) : (
                    <button type="button" className="btn" onClick={() => setJuryInviteSent(true)}
                      style={{ background: '#9B59B6', color: 'white', fontSize: 13 }}>
                      Inviter le membre du jury
                    </button>
                  )
                )}
              </div>
            </form>
          </div>
        )}

        {/* Le mot de passe genere n'est jamais relisible ensuite : il n'est stocke
            que hache. Ce bandeau reste affiche jusqu'a fermeture explicite pour
            que l'administrateur ait le temps de le transmettre. */}
        {generated && (
          <div className="card" style={{ marginBottom: 16, background: '#FFF8E1',
            border: '1px solid #FFE082' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
              <div>
                <strong style={{ color: '#8D6E20', fontSize: 15 }}>
                  {generated.isNew ? 'Compte créé' : 'Nouveau mot de passe généré'} — {generated.email}
                </strong>
                <p style={{ fontSize: 12.5, color: '#8D6E20', margin: '6px 0 12px', lineHeight: 1.6 }}>
                  Transmettez ce mot de passe provisoire à son destinataire.
                  <strong> Il ne sera plus jamais affiché.</strong> À sa première connexion,
                  la personne devra en choisir un nouveau, puis enregistrer son application
                  d'authentification (QR code + codes de secours).
                </p>
                <code style={{ display: 'inline-block', padding: '10px 16px', background: 'white',
                  border: '1px solid #FFCC80', borderRadius: 8, fontSize: 18, letterSpacing: 2,
                  fontFamily: 'monospace' }}>
                  {generated.password}
                </code>
                <button type="button"
                  onClick={() => { navigator.clipboard?.writeText(generated.password); toast.success('Mot de passe copié'); }}
                  style={{ marginLeft: 10, padding: '8px 14px', border: '1px solid #FFCC80',
                    borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12.5,
                    fontFamily: 'inherit', color: '#8D6E20', fontWeight: 600 }}>
                  Copier
                </button>
              </div>
              <button type="button" onClick={() => setGenerated(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20,
                  color: '#8D6E20', lineHeight: 1 }} title="Fermer">×</button>
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            className="form-input" placeholder="Rechercher par nom, email, société ou rôle..."
            style={{ fontSize: 14 }} />
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-light)' }}>
            {filtered.length} utilisateur{filtered.length > 1 ? 's' : ''}{search.trim() ? ` sur ${users.length}` : ''}
          </div>
        </div>

        {/* La colonne MFA et le bouton « Débloquer MFA » ont fait dépasser le
            tableau de la carte : sans conteneur scrollable, les dernières
            colonnes débordaient hors du cadre blanc. */}
        {/* Legende : les icones seules ne sont pas devinables au premier coup
            d'oeil. Le survol donne le detail, cette ligne donne le repere. */}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center',
          marginBottom: 10, fontSize: 12, color: 'var(--text-light)' }}>
          <strong style={{ fontWeight: 600 }}>Actions :</strong>
          {[
            ['🚫', 'Désactiver'],
            ['✅', 'Réactiver'],
            ['🔑', 'Nouveau mot de passe'],
            ['🔓', 'Débloquer le MFA'],
            ['🗑️', 'Supprimer'],
          ].map(([ic, lbl]) => (
            <span key={lbl} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontSize: 13 }}>{ic}</span>{lbl}
            </span>
          ))}
        </div>

        {/* Rembourrage nul : avec le rembourrage de carte, les colonnes
            défilaient dans la bande libre à droite de la colonne d'actions
            collée, et venaient s'afficher à côté d'elle. */}
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table style={{ width: '100%', minWidth: 960, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Utilisateur</th>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '10px 8px', textAlign: 'center' }}>Rôle</th>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Société</th>
                <th style={{ padding: '10px 8px', textAlign: 'center' }}>Statut</th>
                <th style={{ padding: '10px 8px', textAlign: 'center' }}>MFA</th>
                <th style={{ padding: '10px 8px', textAlign: 'center' }}>Dernière connexion</th>
                <th className="col-sticky-right" style={{ padding: '10px 8px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 8px', fontWeight: 500 }}>{u.first_name} {u.last_name}</td>
                  <td style={{ padding: '10px 8px', fontSize: 13 }}>{u.email}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    <span className="badge" style={{ background: `${ROLE_COLORS[u.role]}20`, color: ROLE_COLORS[u.role], fontSize: 11 }}>
                      {ROLE_LABELS[u.role]}
                    </span>
                  </td>
                  <td style={{ padding: '10px 8px', fontSize: 13 }}>{u.company || '-'}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    <span className={`badge badge-${u.is_active ? 'vert' : 'rouge'}`} style={{ fontSize: 11 }}>
                      {u.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{mfaBadge(u)}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: 12, color: 'var(--text-light)' }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="col-sticky-right" style={{ padding: '10px 8px', textAlign: 'center' }}>
                    {/* Toutes les actions sont des icones : quatre boutons texte
                        depassaient la largeur de la carte et imposaient un
                        defilement horizontal pour atteindre la derniere. Chaque
                        icone porte une bulle d'aide au survol. */}
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center',
                      whiteSpace: 'nowrap' }}>
                      <IconAction
                        icon={u.is_active ? '🚫' : '✅'}
                        hint={u.is_active
                          ? `Désactiver le compte : ${u.email} ne pourra plus se connecter`
                          : `Réactiver le compte : ${u.email} pourra se reconnecter`}
                        onClick={() => askToggleActive(u)} />
                      <IconAction
                        icon="🔑"
                        hint="Générer un nouveau mot de passe provisoire, affiché une seule fois"
                        onClick={() => askResetPassword(u)} />
                      {u.mfa_required && (() => {
                        const actionable = u.mfa_enabled || u.mfa_help_pending;
                        return (
                          <IconAction
                            icon="🔓"
                            hint={actionable
                              ? 'Débloquer le MFA : la personne ré-enrôlera son application à la prochaine connexion'
                              : 'Aucun second facteur enrôlé : rien à débloquer'}
                            onClick={() => askResetMfa(u)}
                            disabled={!actionable}
                            borderColor={u.mfa_help_pending ? '#FFCDD2' : undefined}
                            background={u.mfa_help_pending ? '#FFF5F5' : undefined} />
                        );
                      })()}
                      <IconAction
                        icon="🗑️"
                        hint="Supprimer définitivement le compte et toutes ses données"
                        onClick={() => askDelete(u)}
                        danger />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
}
